import { OptionsApi } from '@core/swagger-client';
import {
    AccountVerificationRequired,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    ErrorMessagePage,
    Heading,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Warning
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShareEventWidget } from '../../molecules';

export const ShareWebinar = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();

    const { data: webinar, error: webinarError } = useQuery(OptionsApi.webinar.getEdit(webinarId!));
    const { isLoading: isVerifiedLoading, data: isVerifiedData, error: isVerifiedError } = useQuery(OptionsApi.account.isVerified());
    const { data: broadcasterCode, error: broadcasterCodeError } = useQuery(OptionsApi.webinar.getBroadcasterCode(webinarId!));
    const { data: moderatorCode, error: moderatorCodeError } = useQuery(OptionsApi.webinar.getModeratorCode(webinarId!));
    const { data: securitySettings, error: securitySettingsError } = useQuery(OptionsApi.webinarSecurity.getSettings(webinarId!));

    const [anonymousLink, setAnonymousLink] = useState<string>('');
    const [detailsLink, setDetailsLink] = useState<string>('');
    const [moderatorLink, setModeratorLink] = useState<string>('');
    const [broadcasterLink, setBroadcasterLink] = useState<string>('');

    useEffect(() => {
        if (!webinarId || !moderatorCode || !broadcasterCode || !securitySettings) {
            return;
        }

        setDetailsLink(`${globalThis.window.location.origin}/${webinarId}`);
        setModeratorLink(`${globalThis.window.location.origin}/${webinarId}/m/${moderatorCode}`);
        setBroadcasterLink(`${globalThis.window.location.origin}/${webinarId}/b/${broadcasterCode}`);
        if (securitySettings.allowAnonymousViewers && securitySettings.anonymousViewerAuthorizationCode) {
            setAnonymousLink(`${globalThis.window.location.origin}/${webinarId}/a/${securitySettings.anonymousViewerAuthorizationCode}`);
        }
    }, [webinarId, moderatorCode, broadcasterCode, securitySettings]);

    const error = webinarError ?? isVerifiedError ?? moderatorCodeError ?? broadcasterCodeError ?? securitySettingsError;
    if (error) {
        return <ErrorMessagePage errorSubject="Webinar.ApiErrorCode" apiError={error} />;
    }

    if (!webinar || !moderatorCode || !broadcasterCode || isVerifiedLoading || !securitySettings) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>{webinar && <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />}</PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="overview" />

                <ContentHeader contentHeader={t('Share.PageHeading')} testId="eventContentId" />

                {!isVerifiedData && <AccountVerificationRequired />}

                {isVerifiedData && (
                    <div className="max-w-4xl md:mt-6">
                        <div className="mb-2 mt-2 flex flex-col p-3 bg-white rounded-lg">
                            <span className="text-lg">{t('Share.SignUps')}</span>
                            {/* TODO: Must fix number of sign ups e.g. interested */}
                            <span className="font-medium text-3xl md:text-4xl lg:text-5xl">{0}</span>
                        </div>
                        <Heading variant="h2" classNames="mb-2 md:mb-4 mt-6 md:mt-10 lg:mt-14">
                            {t('Share.InviteParticipants.Heading')}
                        </Heading>
                        <p className="mb-4">{t('Share.InviteParticipants.Text')}</p>
                        <ShareEventWidget url={detailsLink} />
                        {securitySettings.allowAnonymousViewers && !securitySettings.anonymousViewerAuthorizationCode && (
                            <p className="bg-warning-light rounded-md flex items-center mb-4 pl-3 pr-4 py-2">
                                <Warning classNames="w-7 md:w-8 flex-none mr-2 text-warning" />
                                <em>{t('Share.InviteParticipants.AnonymousEnabled.WarningText')}</em>
                            </p>
                        )}
                        {securitySettings.allowAnonymousViewers && securitySettings.anonymousViewerAuthorizationCode && (
                            <>
                                <Heading variant="h2" classNames="mb-2 md:mb-4 mt-6 md:mt-10 lg:mt-14">
                                    {t('Share.InviteAnonymous.Heading')}
                                </Heading>
                                <p className="mb-3">{t('Share.InviteAnonymous.Text')}</p>
                                <p className="bg-warning-light rounded-md flex items-center mb-4 pl-3 pr-4 py-2">
                                    <Warning classNames="w-7 md:w-8 flex-none mr-2 text-warning" />
                                    <em>{t('Share.InviteAnonymous.WarningText')}</em>
                                </p>
                                <ShareEventWidget url={anonymousLink} />
                            </>
                        )}
                        <Heading variant="h2" classNames="mb-2 md:mb-4 mt-6 md:mt-10 lg:mt-14">
                            {t('Share.InviteModerator.Heading')}
                        </Heading>
                        <p className="mb-3">{t('Share.InviteModerator.Text')}</p>
                        <p className="bg-warning-light rounded-md flex items-center mb-4 pl-3 pr-4 py-2">
                            <Warning classNames="w-7 md:w-8 flex-none mr-2 text-warning" />
                            <em>{t('Share.InviteModerator.WarningText')}</em>
                        </p>
                        <ShareEventWidget url={moderatorLink} />
                        <Heading variant="h2" classNames="mb-2 md:mb-4 mt-6 md:mt-10 lg:mt-14">
                            {t('Share.InviteBroadcaster.Heading')}
                        </Heading>
                        <p className="mb-3">{t('Share.InviteBroadcaster.Text')}</p>
                        <p className="bg-warning-light rounded-md flex items-center mb-4 pl-3 pr-4 py-2">
                            <Warning classNames="w-7 md:w-8 flex-none mr-2 text-warning" />
                            <em>{t('Share.InviteBroadcaster.WarningText')}</em>
                        </p>
                        <ShareEventWidget url={broadcasterLink} />
                    </div>
                )}
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
